import { useContext, useEffect, useState } from "react";

import { AppContext } from "../AppContextProvider";
import dealsService from "../services/dealsService";

/**
 * The deal-level FK `Lookup` fields whose stored value is a numeric id and
 * whose human label lives in a separate table. Keyed by the metadata's
 * `distinct_values_lookup` value (brokerage_id -> "brokers_xref", etc.).
 *
 * `deal_status` / `deal_type` are intentionally excluded: their stored value
 * IS the label (LEASE, NEGOTIATING), so they need no resolution.
 */
const SOURCES = [
  {
    key: "brokers_xref",
    fetch: (args) => dealsService.listBrokers(args),
    label: (r) => r.company_name || r.vendor_code,
    fallback: "Broker",
  },
  {
    key: "warwick_landman",
    fetch: (args) => dealsService.listLandmen(args),
    label: (r) => r.full_name,
    fallback: "Landman",
  },
  {
    key: "aois",
    fetch: (args) => dealsService.listAois(args),
    label: (r) => r.name,
    fallback: "AOI",
  },
  {
    // The Buyer/Seller master, behind `deal_entities.entity_id` (label
    // "Entity") and `payments.paid_by_entity_id` ("Paid By").
    //
    // Feedback #60: without this the filter picker found no values for an
    // `entities` Lookup and fell back to a TEXT BOX, per `filterEditorFor`.
    // Typing a company name there sent `entity_id contains "Copperhead"`,
    // which the server compiles to `lower(entity_id) LIKE '%copperhead%'` on
    // a bigint column — Postgres answers "function lower(bigint) does not
    // exist" and the whole grid fails to load. With values present the field
    // gets a name dropdown and sends the id, which is what it always wanted.
    key: "entities",
    fetch: (args) => dealsService.listAllEntities(args),
    label: (r) => r.name,
    fallback: "Entity",
  },
];

/**
 * Fetch the deal-level FK lookup tables (brokers, landmen, AOIs) once and
 * expose them keyed by the metadata's `distinct_values_lookup`.
 *
 * Resolves the "cells show the raw id instead of the name" problem on the
 * read-only surfaces: the deals grid and the Deal detail read view. (The
 * create/edit form resolves its own labels via WigMetadataForm's async
 * LookupSelect + the wired lookupFetchers.) `includeInactive` so an
 * assigned-but-deactivated row still resolves to its name.
 *
 * @returns {{
 *   labelByLookup: Record<string, Record<string|number, string>>,
 *   loading: boolean,
 * }}
 *   - `labelByLookup[lookup][id]` -> display label (grid cells + read view)
 *
 * `enabled: false` fetches nothing, for a caller that was handed the labels
 * by its page (the deals list's map band).
 */
export default function useLookupLabels({ enabled = true } = {}) {
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);
  const [labelByLookup, setLabelByLookup] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
        let tk = token;
        if (!tk || isTokenExpired(tk)) tk = await getToken();

        const results = await Promise.all(
          SOURCES.map((s) =>
            s
              .fetch({ token: tk, userOid, includeInactive: true })
              .catch(() => null)
          )
        );
        if (cancelled) return;

        const labels = {};
        results.forEach((resp, i) => {
          const s = SOURCES[i];
          const rows = resp?.data?.data || resp?.data || [];
          const map = {};
          if (Array.isArray(rows)) {
            for (const r of rows) {
              if (r?.id == null) continue;
              map[r.id] = s.label(r) || `${s.fallback} ${r.id}`;
            }
          }
          labels[s.key] = map;
        });
        setLabelByLookup(labels);
      } catch (err) {
        // Non-fatal — callers fall back to rendering the raw id.
        console.error("Lookup label fetch failed:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Auth context is closed over; the lookup tables are stable so we fetch
    // once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { labelByLookup, loading };
}
